import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
// ANGULAR IMPORTS EVERY MATERIAL SYMBOLS FACE; THE TEMPLATES USE ROUNDED AND OUTLINED
// (`task_alt`, `notifications`). BOTH ARE BUNDLED BY VITE, NEVER FETCHED FROM A CDN.
import "material-symbols/rounded.css";
import "material-symbols/outlined.css";
// THE PILLARS THEME TOKENS, THEN THE ANGULAR GLOBAL STYLES (LAYOUT PARTIALS + PRIMEFLEX
// 3.3.1 COMPILED WITH ANGULAR'S 1REM GUTTER) - SEE `styles/pillars-layout.scss`
import "./styles/pillars-theme-vars.css";
import "./styles/pillars-layout.scss";
import "./styles/pillars-components.css";
import "./fonts.css";
import { theme } from "./theme";
import { buildRouter } from "./router";
import { setUnauthenticatedHandler } from "./api/client";
import { clearSession } from "./auth/token";
import { ToastProvider } from "./ui/toast";
import { clearStoredSections } from "./floor-sections/floor-sections";
import { appConfigStorageManager, MODE_STORAGE_KEY, storedColorMode } from "./ui/color-mode";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: true },
  },
});

const router = buildRouter(queryClient);

// A MISSING OR REJECTED TOKEN ENDS THE SESSION: FORGET IT, DROP EVERY CACHED
// RESPONSE, AND GO TO LOGIN
setUnauthenticatedHandler(() => {
  clearStoredSections();
  clearSession();
  queryClient.clear();
  void router.navigate({ to: "/login" });
});

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

createRoot(root).render(
  <StrictMode>
    <ThemeProvider
      theme={theme}
      defaultMode={storedColorMode()}
      modeStorageKey={MODE_STORAGE_KEY}
      storageManager={appConfigStorageManager}
      disableTransitionOnChange
      // A CLIENT-ONLY APP: KNOW THE MODE ON THE FIRST RENDER RATHER THAN AFTER MOUNT
      noSsr
    >
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <RouterProvider router={router} />
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
